import { useMutation, useQuery } from "@tanstack/react-query";
import { usePost, useGet } from "../../../common/api-service";
import { UserFormSchema } from "../../../common/components/user-invitation-modal/api/schema";
import {
  ConfirmPasswordPayload,
  ValidateEmailPayload,
  SendEmailOTPCodePayload,
  ValidateNumberPayload,
  SendNumberOTPCodePayload,
  UpdateMobileNumberPayload,
  UpdateEmailPayload,
  UpdatePasswordPayload,
  UpdateTwoFactorEnabled,
} from "./types";
import { signString } from "../../../common/utils/string-signing";
import { enqueueSnackbar } from "notistack";
import axios from "axios";

export const useUpdateUser = () => {
  const { post } = usePost();
  return useMutation({
    mutationFn: async (data: UserFormSchema) => {
      data.email = "";
      const formData = convertUserFormSchemaToFormData(data);
      return post(`/api/user-account/update-user`, formData, true, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
  });
};

const convertUserFormSchemaToFormData = (data: UserFormSchema): FormData => {
  const formData = new FormData();

  formData.append("Email", data.email ?? "");
  formData.append("FirstName", data.firstName);
  formData.append("LastName", data.lastName);
  if (data.birthDate) formData.append("BirthDate", data.birthDate);
  if (data.id) formData.append("Id", data.id);
  if (data.civilStatus)
    formData.append("CivilStatus", data.civilStatus.toString());
  if (data.gender) formData.append("Gender", data.gender.toString());

  if (data.prefix) formData.append("Prefix", data.prefix);
  if (data.suffix) formData.append("Suffix", data.suffix);
  if (data.customName) formData.append("CustomName", data.customName);
  if (data.middleName) formData.append("MiddleName", data.middleName);
  if (data.photoUrl) formData.append("UserPhoto", data.photoUrl);

  return formData;
};

export const useConfirmPassword = () => {
  const { post } = usePost();

  return useMutation({
    mutationFn: async (data: ConfirmPasswordPayload) => {
      return post(`/api/user-account/confirm-password`, data, false, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    onError(error, variables, context) {
      if (axios.isAxiosError(error) && error.response) {
        const message = error.response.data || "Password confirmation failed.";
        enqueueSnackbar(message, { variant: "error" });
      }
    },
  });
};

export const useValidateEmail = () => {
  const { post } = usePost();

  return useMutation<boolean, Error, ValidateEmailPayload>({
    mutationFn: async (data: ValidateEmailPayload) => {
      const res = await post(`/api/user-account/validate-email`, data, false, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      return res as boolean;
    },
  });
};

export const useSendEmailOTPCode = () => {
  const { post } = usePost();

  return useMutation({
    mutationFn: async (data: SendEmailOTPCodePayload) => {
      return post(`/api/user-account/send-email-otp`, data, false, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
  });
};

export const useValidateNumber = () => {
  const { post } = usePost();

  return useMutation<boolean, Error, ValidateNumberPayload>({
    mutationFn: async (data: ValidateNumberPayload) => {
      const res = await post(
        `/api/user-account/validate-phone-number`,
        data,
        false,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      return res as boolean;
    },
  });
};

export const useSendNumberOTPCode = () => {
  const { post } = usePost();

  return useMutation({
    mutationFn: async (data: SendNumberOTPCodePayload) => {
      return post(`/api/user-account/send-number-otp`, data, false, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
  });
};

export const useUpdateMobileNumber = () => {
  const { post } = usePost();

  return useMutation({
    mutationFn: async (data: UpdateMobileNumberPayload) => {
      return post("/api/user-account/update-phone-number", data, false, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
  });
};

export const useUpdateEmail = () => {
  const { post } = usePost();

  return useMutation({
    mutationFn: async (data: UpdateEmailPayload) => {
      data.emailSignature = await signString(data.email);

      return post("/api/user-account/update-email", data, false, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
  });
};

export const useUpdatePassword = () => {
  const { post } = usePost();

  return useMutation({
    mutationFn: async (data: UpdatePasswordPayload) => {
      data.passwordSignature = await signString(data.newPassword);

      return post("/api/user-account/update-password", data, false, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    onSuccess(data, variables, context) {
      enqueueSnackbar("Password updated successfully!", {
        variant: "success",
      });
    },
    onError(error, variables, context) {
      if (axios.isAxiosError(error) && error.response) {
        const message = error.response.data || "Failed to update password.";
        enqueueSnackbar(message, { variant: "error" });
      }
    },
  });
};

export const useUpdateTwoFactorEnabled = () => {
  const { post } = usePost();

  return useMutation({
    mutationFn: async (data: UpdateTwoFactorEnabled) => {
      return post("/api/user-account/update-two-factor-enabled", data, false, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
  });
};

// --- Authenticator (TOTP) MFA — feature/totp-mfa ---
export interface AuthenticatorStatus {
  enabled: boolean;
  eligible: boolean;
  required: boolean;
}
export const AUTHENTICATOR_STATUS_KEY = ["authenticator-status"];

export const useAuthenticatorStatus = () => {
  const { get } = useGet();
  return useQuery<AuthenticatorStatus>({
    queryKey: AUTHENTICATOR_STATUS_KEY,
    queryFn: async () => get("/api/user/mfa/authenticator/status"),
    staleTime: 30_000,
  });
};

export interface AuthenticatorEnrollStart {
  otpAuthUri: string;
  manualEntryKey: string;
}
export interface AuthenticatorEnrollResult {
  recoveryCodes: string[];
}

export const useAuthenticatorEnrollStart = () => {
  const { post } = usePost();
  return useMutation<AuthenticatorEnrollStart, Error, void>({
    mutationFn: async () =>
      post("/api/user/mfa/authenticator/enroll/start", {}, false),
  });
};

export const useAuthenticatorEnrollVerify = () => {
  const { post } = usePost();
  return useMutation<AuthenticatorEnrollResult, Error, { code: string }>({
    mutationFn: async (data: { code: string }) =>
      post("/api/user/mfa/authenticator/enroll/verify", { Code: data.code }, false),
  });
};

export const useDisableAuthenticator = () => {
  const { post } = usePost();
  return useMutation<string, Error, { code: string }>({
    mutationFn: async (data: { code: string }) =>
      post("/api/user/mfa/authenticator/disable", { Code: data.code }, false),
  });
};
